import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';
import TradingModal from '../trading/TradingModal';
import BotConfigModal from '../tokens/BotConfigModal';
import PriceAlertModal from '../alerts/PriceAlertModal';
import PriceTargetModal from '../tokens/PriceTargetModal';
import AddTokenModal from '../tokens/AddTokenModal';
import '../../styles/solana-tokens-dashboard.css';
import { CopyableMint } from '../common/CopyButton';
import SafeBalanceDisplay from '../common/SafeBalanceDisplay';
import QuickTradeActions from '../trading/QuickTradeActions';
import { apiClient } from '../../utils/apiClient';

const DEXSCREENER_API_BASE = 'https://api.dexscreener.com';

// Batch fetch price data from DexScreener for multiple tokens at once.
// DexScreener supports up to 30 addresses per request separated by commas.
const fetchBatchPriceData = async (mintAddresses) => {
  const result = {};
  if (!mintAddresses || mintAddresses.length === 0) return result;

  const BATCH_SIZE = 30;
  const batches = [];
  for (let i = 0; i < mintAddresses.length; i += BATCH_SIZE) {
    batches.push(mintAddresses.slice(i, i + BATCH_SIZE));
  }

  await Promise.all(
    batches.map(async (batch) => {
      try {
        const response = await fetch(
          `${DEXSCREENER_API_BASE}/latest/dex/tokens/${batch.join(',')}`
        );
        const data = await response.json();
        if (data.pairs && data.pairs.length > 0) {
          // Group pairs by base token address
          const pairsByToken = {};
          for (const pair of data.pairs) {
            const addr = pair.baseToken?.address;
            if (!addr) continue;
            if (!pairsByToken[addr] || (pair.liquidity?.usd || 0) > (pairsByToken[addr].liquidity?.usd || 0)) {
              pairsByToken[addr] = pair;
            }
          }
          for (const [addr, pair] of Object.entries(pairsByToken)) {
            result[addr] = {
              price: parseFloat(pair.priceUsd) || 0,
              change24h: parseFloat(pair.priceChange?.h24) || 0,
              volume24h: parseFloat(pair.volume?.h24) || 0,
              marketCap: parseFloat(pair.fdv) || 0
            };
          }
        }
      } catch (error) {
        console.error('Error fetching batch price data:', error);
      }
    })
  );

  return result;
};

const EMPTY_PRICE = { price: 0, change24h: 0, volume24h: 0, marketCap: 0 };

const SolanaTokensDashboard = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [tradingModal, setTradingModal] = useState({
    isOpen: false,
    token: null,
    tradeType: 'buy'
  });
  const [botConfigModal, setBotConfigModal] = useState({ isOpen: false, token: null });
  const [alertModal, setAlertModal] = useState({ isOpen: false, token: null });
  const [priceTargetModal, setPriceTargetModal] = useState({ isOpen: false, token: null, type: 'buy' });
  const [addTokenModal, setAddTokenModal] = useState({ isOpen: false });
  const [error, setError] = useState(null);
  const [platformFilter, setPlatformFilter] = useState('all'); // 'all', 'new', 'dexscreener', 'pumpfun', 'bonkfun', etc.
  const [lastUpdated, setLastUpdated] = useState(null);
  const { theme, isLight, isDark } = useTheme();
  const { showSuccess, showInfo, showError } = useToast();
  const { activeWallet } = useWallet();
  // import CopyableMint from '../wallet/CopyableMint';

  // Handle opening trading modal
  const openTradingModal = (token, tradeType) => {
    setTradingModal({
      isOpen: true,
      token: token,
      tradeType: tradeType
    });
  };

  // Handle closing trading modal
  const closeTradingModal = () => {
    setTradingModal({
      isOpen: false,
      token: null,
      tradeType: 'buy'
    });
  };

  // Handle trade completion
  const handleTradeComplete = (tradeData) => {
    showSuccess(`Trade completed successfully!`);
    // Could add more sophisticated handling here like refreshing balances
    console.log('Trade completed:', tradeData);
    closeTradingModal();
  };

  // Modal handler functions
  const openBotConfig = (token) => {
    setBotConfigModal({ isOpen: true, token });
  };

  const closeBotConfig = () => {
    setBotConfigModal({ isOpen: false, token: null });
  };

  const openAlertModal = (token) => {
    setAlertModal({ isOpen: true, token });
  };

  const closeAlertModal = () => {
    setAlertModal({ isOpen: false, token: null });
  };

  const openPriceTargetModal = (token, type = 'buy') => {
    setPriceTargetModal({ isOpen: true, token, type });
  };

  const closePriceTargetModal = () => {
    setPriceTargetModal({ isOpen: false, token: null, type: 'buy' });
  };

  const openAddTokenModal = () => {
    setAddTokenModal({ isOpen: true });
  };

  const closeAddTokenModal = () => {
    setAddTokenModal({ isOpen: false });
  };

  // Refresh tokens manually
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);

      // Fetch all three sources in parallel
      const [adminResponse, submittedResponse, trendingResponse] = await Promise.allSettled([
        apiClient.get('/admin/tokens').catch(() => null),
        apiClient.get('/tokens/submissions').catch(() => null),
        apiClient.get(platformFilter === 'new' ? '/trending/new?limit=50' : '/trending/tokens?limit=50').catch(() => null)
      ]);

      const adminData = adminResponse.status === 'fulfilled' ? adminResponse.value : null;
      const submittedData = submittedResponse.status === 'fulfilled' ? submittedResponse.value : null;
      const trendingData = trendingResponse.status === 'fulfilled' ? trendingResponse.value : null;

      const adminTokensRaw = (adminData?.success && adminData?.tokens) ? adminData.tokens : [];
      const submittedTokensRaw = (submittedData?.success && submittedData?.submissions) ? submittedData.submissions : [];

      const mintAddressesForPricing = [
        ...adminTokensRaw.map(t => t.mint_address),
        ...submittedTokensRaw.map(t => t.mint_address)
      ].filter(Boolean);

      const priceMap = mintAddressesForPricing.length > 0
        ? await fetchBatchPriceData(mintAddressesForPricing)
        : {};

      const adminTokens = adminTokensRaw.map((token) => {
        const priceData = priceMap[token.mint_address] || EMPTY_PRICE;
        return {
          mint: token.mint_address,
          symbol: token.symbol,
          name: token.name,
          decimals: token.decimals,
          logo: token.logo_uri || '/images/unknown-token.png',
          price: priceData.price,
          change24h: priceData.change24h,
          volume24h: priceData.volume24h,
          marketCap: priceData.marketCap,
          description: '',
          coingeckoId: null,
          address: token.mint_address,
          isAdminListed: true,
          isAdminApproved: true
        };
      });

      const submittedTokens = submittedTokensRaw.map((token) => {
        const priceData = priceMap[token.mint_address] || EMPTY_PRICE;
        return {
          mint: token.mint_address,
          symbol: token.symbol || 'UNKNOWN',
          name: token.name || 'Unknown Token',
          decimals: token.decimals || 6,
          logo: token.logo_uri || '/images/unknown-token.png',
          price: priceData.price,
          change24h: priceData.change24h,
          volume24h: priceData.volume24h,
          marketCap: priceData.marketCap,
          description: token.description || '',
          coingeckoId: null,
          address: token.mint_address,
          isAdminListed: token.status === 'approved',
          isAdminApproved: token.status === 'approved',
          isSubmitted: true,
          submissionId: token.id,
          submittedBy: token.submitted_by,
          status: token.status
        };
      });

      let tokensWithPrices = [];
      if (trendingData?.success && trendingData?.data?.tokens) {
        tokensWithPrices = trendingData.data.tokens.map(token => ({
          mint: token.token_address || '',
          symbol: token.token_symbol || 'UNKNOWN',
          name: token.token_name || 'Unknown Token',
          decimals: 6,
          logo: token.current_data?.image_url || '/images/unknown-token.png',
          price: token.current_data?.price || 0,
          change24h: token.current_data?.price_change_24h || 0,
          volume24h: token.current_data?.volume_24h || 0,
          marketCap: token.current_data?.market_cap || 0,
          description: token.current_data?.description || '',
          coingeckoId: null,
          address: token.token_address || '',
          source: token.detection_source || 'unknown',
          trendScore: token.trend_score || 0
        }));
      }

      const adminMintSet = new Set(adminTokens.map(t => t.mint));
      const submittedMintSet = new Set(submittedTokens.map(t => t.mint));
      let mergedTokens = [
        ...adminTokens,
        ...submittedTokens.filter(t => !adminMintSet.has(t.mint)),
        ...tokensWithPrices.filter(t => !adminMintSet.has(t.mint) && !submittedMintSet.has(t.mint))
      ];

      if (platformFilter !== 'all') {
        if (platformFilter === 'submitted') {
          mergedTokens = mergedTokens.filter(token => token.isSubmitted);
        } else if (platformFilter === 'approved') {
          mergedTokens = mergedTokens.filter(token => token.isAdminApproved);
        } else {
          mergedTokens = mergedTokens.filter(token => {
            const source = token.source || 'unknown';
            return source.toLowerCase() === platformFilter.toLowerCase();
          });
        }
      }

      setTokens(mergedTokens);
      setLastUpdated(trendingData?.data?.lastUpdated || new Date().toISOString());
      showSuccess('Tokens refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing trending tokens:', error);
      setError('Failed to refresh trending tokens');
      showError('Failed to refresh tokens');
    } finally {
      setRefreshing(false);
    }
  };

  // Get source badge color and icon
  const getSourceBadge = (source) => {
    const sourceConfig = {
      birdeye: { color: '#8b5cf6', icon: '🦅', label: 'Birdeye' },
      helius: { color: '#06b6d4', icon: '⚡', label: 'Helius' },
      solscan: { color: '#f59e0b', icon: '🔍', label: 'Solscan' },
      dexscreener: { color: '#00d4aa', icon: '📊', label: 'DexScreener' },
      pumpfun: { color: '#ff6b6b', icon: '🚀', label: 'Pump.fun' },
      bonkfun: { color: '#ffd93d', icon: '🐕', label: 'Bonk.fun' },
      raydium: { color: '#7c3aed', icon: '🌊', label: 'Raydium' },
      orca: { color: '#06b6d4', icon: '🐳', label: 'Orca' },
      jupiter: { color: '#f59e0b', icon: '🪐', label: 'Jupiter' },
      on_chain: { color: '#10b981', icon: '⛓️', label: 'On-Chain' },
      unknown: { color: '#6b7280', icon: '❓', label: 'Unknown' }
    };

    const config = sourceConfig[source?.toLowerCase()] || sourceConfig.unknown;
    return config;
  };

  // Popular Solana tokens with DexScreener data
  const popularSolanaTokens = [
    {
      symbol: 'SOL',
      name: 'Solana',
      address: 'So11111111111111111111111111111111111111112',
      price: 167.33,
      change24h: -7.58,
      volume24h: 787000000,
      marketCap: 78000000000,
      logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
      description: 'High-performance blockchain platform for decentralized applications.'
    },
    {
      symbol: 'BONK',
      name: 'Bonk',
      address: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
      price: 0.000023,
      change24h: 12.45,
      volume24h: 125000000,
      marketCap: 1500000000,
      logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263/logo.png',
      description: 'Community-driven meme token on Solana.'
    },
    {
      symbol: 'JUP',
      name: 'Jupiter',
      address: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',
      price: 0.87,
      change24h: 5.23,
      volume24h: 89000000,
      marketCap: 870000000,
      logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN/logo.png',
      description: 'Leading DEX aggregator on Solana.'
    },
    {
      symbol: 'WIF',
      name: 'dogwifhat',
      address: 'EKpQGSJtjGqZ3QzM16w68AM1QhgHLnkicU9S2yB7b5dC',
      price: 2.34,
      change24h: -3.21,
      volume24h: 45000000,
      marketCap: 2340000000,
      logo: 'https://bafkreibk3covs5lty62ia3kbl3c3u4rnin2l5i75m4uu7qxq2pxcj4k2mi.ipfs.nftstorage.link/',
      description: 'Popular meme token featuring a dog with a hat.'
    },
    {
      symbol: 'BOME',
      name: 'BOOK OF MEME',
      address: 'ukHH6c7mMyiWCf1b9pnWe25TSpkDDt3H5pQZgZ74J82',
      price: 0.0089,
      change24h: 8.76,
      volume24h: 67000000,
      marketCap: 614000000,
      logo: 'https://bafkreid2cy6jreh6q4j5dpqhqvcuej5k4h2e7pl4y4y4y4y4y4y4y4y4y4.ipfs.nftstorage.link/',
      description: 'Meme culture preservation token.'
    },
    {
      symbol: 'POPCAT',
      name: 'Popcat',
      address: '6GCVwNNab4EakzchaS9zAFSDAqKYFpkjAKgQ4dVw9xaR',
      price: 0.45,
      change24h: 15.32,
      volume24h: 23000000,
      marketCap: 450000000,
      logo: 'https://bafkreiec4f3xqlzqijwj2e4dqaz5ya6r7qrr2clawc5z2d2x4e4e4e4e4e4.ipfs.nftstorage.link/',
      description: 'Cat-themed meme token.'
    },
    {
      symbol: 'MEW',
      name: 'cat in a dogs world',
      address: 'MEW1gQWJ3nEXg2qgEiDHUrNnFuvX7MZgzzU8LuTq3K9',
      price: 0.0045,
      change24h: -2.11,
      volume24h: 34000000,
      marketCap: 400000000,
      logo: 'https://bafkreid2cy6jreh6q4j5dpqhqvcuej5k4h2e7pl4y4y4y4y4y4y4y4y4.ipfs.nftstorage.link/',
      description: 'Cat-themed meme token in the Solana ecosystem.'
    },
    {
      symbol: 'WEN',
      name: 'Wen',
      address: 'WENWENvqqNya429ubCdR81ZmD69brwQaaBYY6p3LCpk',
      price: 0.000089,
      change24h: 4.56,
      volume24h: 12000000,
      marketCap: 89000000,
      logo: 'https://bafkreiec4f3xqlzqijwj2e4dqaz5ya6r7qrr2clawc5z2d2x4e4e4e4e4e4.ipfs.nftstorage.link/',
      description: 'Community-driven meme token.'
    },
    {
      symbol: 'SLERF',
      name: 'SLERF',
      address: '7BgBvyjrZX1KmxbUv5nf7iwS2t4W7uS1vPjRhwy2kz7d',
      price: 0.12,
      change24h: -8.43,
      volume24h: 18000000,
      marketCap: 120000000,
      logo: 'https://bafkreid2cy6jreh6q4j5dpqhqvcuej5k4h2e7pl4y4y4y4y4y4y4y4y4.ipfs.nftstorage.link/',
      description: 'Sloth-themed meme token.'
    },
    {
      symbol: 'MYRO',
      name: 'Myro',
      address: 'myRoQat2xWR2xYbQ5V92WwGpU6TuP7tMEo8YpPgRu1S',
      price: 0.045,
      change24h: 6.78,
      volume24h: 8900000,
      marketCap: 45000000,
      logo: 'https://bafkreiec4f3xqlzqijwj2e4dqaz5ya6r7qrr2clawc5z2d2x4e4e4e4e4e4.ipfs.nftstorage.link/',
      description: 'Dog-themed meme token.'
    }
  ];

  useEffect(() => {
    // Fetch live trending tokens from backend API and admin token listings
    const fetchTokens = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all three data sources in parallel
        const [adminResponse, submittedResponse, trendingResponse] = await Promise.allSettled([
          apiClient.get('/admin/tokens').catch(() => null),
          apiClient.get('/tokens/submissions').catch(() => null),
          apiClient.get(platformFilter === 'new' ? '/trending/new?limit=50' : '/trending/tokens?limit=50').catch(() => null)
        ]);

        const adminData = adminResponse.status === 'fulfilled' ? adminResponse.value : null;
        const submittedData = submittedResponse.status === 'fulfilled' ? submittedResponse.value : null;
        const trendingData = trendingResponse.status === 'fulfilled' ? trendingResponse.value : null;

        // Collect all mint addresses that need price data from DexScreener
        const adminTokensRaw = (adminData?.success && adminData?.tokens) ? adminData.tokens : [];
        const submittedTokensRaw = (submittedData?.success && submittedData?.submissions) ? submittedData.submissions : [];

        const mintAddressesForPricing = [
          ...adminTokensRaw.map(t => t.mint_address),
          ...submittedTokensRaw.map(t => t.mint_address)
        ].filter(Boolean);

        // Batch fetch all prices in one go
        const priceMap = mintAddressesForPricing.length > 0
          ? await fetchBatchPriceData(mintAddressesForPricing)
          : {};

        // Build admin tokens
        const adminTokens = adminTokensRaw.map((token) => {
          const priceData = priceMap[token.mint_address] || EMPTY_PRICE;
          return {
            mint: token.mint_address,
            symbol: token.symbol,
            name: token.name,
            decimals: token.decimals,
            logo: token.logo_uri || '/images/unknown-token.png',
            price: priceData.price,
            change24h: priceData.change24h,
            volume24h: priceData.volume24h,
            marketCap: priceData.marketCap,
            description: '',
            coingeckoId: null,
            address: token.mint_address,
            isAdminListed: true,
            isAdminApproved: true
          };
        });

        // Build submitted tokens
        const submittedTokens = submittedTokensRaw.map((token) => {
          const priceData = priceMap[token.mint_address] || EMPTY_PRICE;
          return {
            mint: token.mint_address,
            symbol: token.symbol || 'UNKNOWN',
            name: token.name || 'Unknown Token',
            decimals: token.decimals || 6,
            logo: token.logo_uri || '/images/unknown-token.png',
            price: priceData.price,
            change24h: priceData.change24h,
            volume24h: priceData.volume24h,
            marketCap: priceData.marketCap,
            description: token.description || '',
            coingeckoId: null,
            address: token.mint_address,
            isAdminListed: token.status === 'approved',
            isAdminApproved: token.status === 'approved',
            isSubmitted: true,
            submissionId: token.id,
            submittedBy: token.submitted_by,
            status: token.status
          };
        });

        // Build trending tokens (prices already embedded in backend response)
        let tokensWithPrices = [];
        if (trendingData?.success && trendingData?.data?.tokens) {
          tokensWithPrices = trendingData.data.tokens.map(token => ({
            mint: token.token_address || '',
            symbol: token.token_symbol || 'UNKNOWN',
            name: token.token_name || 'Unknown Token',
            decimals: 6,
            logo: token.current_data?.image_url || '/images/unknown-token.png',
            price: token.current_data?.price || 0,
            change24h: token.current_data?.price_change_24h || 0,
            volume24h: token.current_data?.volume_24h || 0,
            marketCap: token.current_data?.market_cap || 0,
            description: token.current_data?.description || '',
            coingeckoId: null,
            address: token.token_address || '',
            isAdminListed: false,
            source: token.detection_source || 'unknown',
            trendScore: token.trend_score || 0
          }));
        }

        // Merge, deduplicating by mint address
        const adminMintSet = new Set(adminTokens.map(t => t.mint));
        const submittedMintSet = new Set(submittedTokens.map(t => t.mint));
        let mergedTokens = [
          ...adminTokens,
          ...submittedTokens.filter(t => !adminMintSet.has(t.mint)),
          ...tokensWithPrices.filter(t => !adminMintSet.has(t.mint) && !submittedMintSet.has(t.mint))
        ];

        // Apply platform filter
        if (platformFilter !== 'all') {
          if (platformFilter === 'submitted') {
            mergedTokens = mergedTokens.filter(token => token.isSubmitted);
          } else if (platformFilter === 'approved') {
            mergedTokens = mergedTokens.filter(token => token.isAdminApproved);
          } else {
            mergedTokens = mergedTokens.filter(token => {
              const source = token.source || token.detection_source || 'unknown';
              return source.toLowerCase() === platformFilter.toLowerCase();
            });
          }
        }

        if (mergedTokens.length === 0) {
          const fallbackTokens = popularSolanaTokens.slice(0, 10).map(token => ({
            ...token,
            mint: token.address,
            address: token.address,
            isAdminListed: false
          }));
          setTokens(fallbackTokens);
        } else {
          setTokens(mergedTokens);
        }

        setLastUpdated(trendingData?.data?.lastUpdated || new Date().toISOString());
        setLoading(false);
      } catch (error) {
        console.error('Error fetching trending tokens:', error);
        const fallbackTokens = popularSolanaTokens.slice(0, 10).map(token => ({
          ...token,
          mint: token.address,
          address: token.address,
          isAdminListed: false
        }));
        setTokens(fallbackTokens);
        setError(null);
        setLoading(false);
      }
    };

    fetchTokens();
  }, [platformFilter]);

  const formatPrice = (price) => {
    if (price < 0.01) {
      return `$${price.toFixed(8)}`;
    }
    return `$${price.toFixed(4)}`;
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap >= 1e9) {
      return `$${(marketCap / 1e9).toFixed(2)}B`;
    } else if (marketCap >= 1e6) {
      return `$${(marketCap / 1e6).toFixed(2)}M`;
    }
    return `$${marketCap.toLocaleString()}`;
  };

  const formatVolume = (volume) => {
    if (volume >= 1e6) {
      return `$${(volume / 1e6).toFixed(1)}M`;
    }
    return `$${volume.toLocaleString()}`;
  };

  const handleTokenClick = (token) => {
    setSelectedToken(token);
  };

  const closeModal = () => {
    setSelectedToken(null);
  };

  if (loading) {
    return (
      <div className={`solana-dashboard ${isDark ? 'dark' : 'light'}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2>Loading Trending Tokens...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`solana-dashboard ${isDark ? 'dark' : 'light'}`}>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h7>Failed to Load Tokens</h7>
          <h6>{error}</h6>
          <button
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`solana-dashboard ${isDark ? 'dark' : 'light'}`}>
      <div className="dashboard-header"
        style={{
          // background: 'blue'
          // backgroundColor: isDark ? 'green' : '#f3f4f6'
        }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', gap: '1rem' }}>
          <div>
            <h1>Trending Solana Tokens</h1>
            <h6 style={{ fontSize: '10.7px', color: isDark ? '#f1f5f9' : '#1e293b' }}>Live trending tokens with real-time prices and market data</h6>
            {lastUpdated && (
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Last updated: {new Date(lastUpdated).toLocaleString()}
              </small>
            )}
          </div>
          <div style={{
            marginLeft: 'auto', display: 'flex',
            alignItems: 'center', gap: '15px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            width: '100%'
          }}>
            <SafeBalanceDisplay
              compact={true}
              style={{
                // color: isDark ? '#f1f5f9' : '#1e293b',
                color: 'green',
                backgroundColor: isDark ? '#374151' : '#f3f4f6',
                border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                padding: '8px 12px',
                flexShrink: 0
              }}
            />
            <button
              className="add-token-btn"
              onClick={openAddTokenModal}
              style={{
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '300',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}
            >
              ➕
              <span>Add Token</span>
            </button>
            <button
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                padding: '8px 16px',
                backgroundColor: refreshing ? '#6b7280' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}
            >
              <span>
                {refreshing ? '🔄' : '🔄'} {refreshing ? 'Refreshing...' : 'Refresh'}
              </span>
            </button>

          </div>
        </div>


        {/* Platform Filter Controls */}
        <div className="platform-filters">
          <span>Filter by Platform:</span>
          {[
            { key: 'all', label: 'All Platforms', icon: '🌐' },
            { key: 'new', label: 'New Tokens', icon: '🆕' },
            { key: 'submitted', label: 'Submitted', icon: '📝' },
            { key: 'approved', label: 'Admin Approved', icon: '✅' },
            { key: 'dexscreener', label: 'DexScreener', icon: '📊' },
            { key: 'pumpfun', label: 'Pump.fun', icon: '🚀' },
            { key: 'bonkfun', label: 'Bonk.fun', icon: '🐕' },
            { key: 'raydium', label: 'Raydium', icon: '🌊' },
            { key: 'orca', label: 'Orca', icon: '🐳' },
            { key: 'jupiter', label: 'Jupiter', icon: '🪐' },
            { key: 'on_chain', label: 'On-Chain', icon: '⛓️' }
          ].map(platform => (
            <button
              key={platform.key}
              onClick={() => setPlatformFilter(platform.key)}
              className={platformFilter === platform.key ? 'active' : ''}
            >
              <span>{platform.icon} {platform.label}</span>
            </button>
          ))}
        </div>
      </div>



      <div className="tokens-grid">
        {tokens.map((token, index) => {
          const sourceBadge = getSourceBadge(token.source || token.detection_source);
          return (
            <div
              key={token.symbol}
              className="token-card"
              onClick={() => handleTokenClick(token)}
            >
              <div className="token-rank">#{index + 1}</div>

              {/* Admin Approved Badge */}
              {token.isAdminApproved && (
                <div className="admin-approved-badge" style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <span style={{ fontSize: '12px' }}>✅</span>
                  <span>Admin Approved</span>
                </div>
              )}

              {/* Submitted Badge */}
              {token.isSubmitted && !token.isAdminApproved && (
                <div className="submitted-badge" style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <span style={{ fontSize: '12px' }}>⏳</span>
                  <span>Pending Review</span>
                </div>
              )}

              {/* Source Badge */}
              <div className="source-badge" style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: sourceBadge.color,
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                zIndex: 10
              }}>
                <span>{sourceBadge.icon}</span>
                <span>{sourceBadge.label}</span>
              </div>

              <div className="token-header">
                <img
                  src={token.logo || '/images/unknown-token.png'}
                  alt={token.name}
                  className="token-logo"
                  onError={(e) => {
                    if (e.target.src !== 'https://via.placeholder.com/40x40/6366f1/ffffff?text=' + (token.symbol ? token.symbol.charAt(0) : '?')) {
                      e.target.src = 'https://via.placeholder.com/40x40/6366f1/ffffff?text=' + (token.symbol ? token.symbol.charAt(0) : '?');
                    }
                  }}
                />
                <div className="token-info" style={{ display: 'flex', flexDirection: 'column' }}>
                  <h3 className="token-name">{token.name}</h3>
                  <span className="token-symbol">{token.symbol}</span>
                </div>
              </div>

              <div className="stat-row" style={{
                display: 'flex',
                flexDirection: 'row',
              }}>
                <span className="stat-label">Price</span>
                <span className="stat-value" style={{
                  fontSize: 15,
                }}>{formatPrice(token.price)}</span>
              </div>

              <div className="token-stats" style={
                {
                  display: 'flex', flexDirection: 'row'
                }
              }>

                <div className="stat-row" style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <span className="stat-label" style={{
                    fontSize: 10.7,
                  }}>24h Change</span>
                  <span className={`stat-value ${token.change24h >= 0 ? 'positive' : 'negative'}`}
                    style={{
                      fontSize: 15,
                    }}>
                    {token.change24h >= 0 ? '+' : ''}{token.change24h.toFixed(2)}%
                  </span>
                </div>
                <div className="stat-row" style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <span className="stat-label" style={{
                    fontSize: 10.7,
                  }}>Market Cap</span>
                  <span className="stat-value" style={{
                    fontSize: 15,
                  }}>{formatMarketCap(token.marketCap)}</span>
                </div>
                <div className="stat-row" style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <span className="stat-label"
                    style={{
                      fontSize: 10.7,
                    }}
                  >24h Volume</span>
                  <span className="stat-value" style={{
                    fontSize: 15,
                  }}>{formatVolume(token.volume24h)}</span>
                </div>
              </div>
              <QuickTradeActions token={token} onTradeComplete={handleTradeComplete} />
              <div className="token-actions">
                <button
                  className="action-btn buy-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openTradingModal(token, 'buy');
                  }}
                >
                  📈 Buy {token.symbol}
                </button>
                <button
                  className="action-btn sell-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openTradingModal(token, 'sell');
                  }}
                >
                  📉 Sell  {token.symbol}
                </button>
                <button
                  className="action-btn bot-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openBotConfig(token);
                  }}
                  disabled={!activeWallet}
                >
                  🤖 Add to Bot
                </button>
                <button
                  className="action-btn alert-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAlertModal(token);
                  }}
                >
                  🔔 Set Alert
                </button>
                <button
                  className="action-btn target-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPriceTargetModal(token, 'buy');
                  }}
                >
                  🎯 Price Target
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selectedToken && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-header">
              <img src={selectedToken.logo} alt={selectedToken.name} className="modal-logo" />
              <div>
                <h2>{selectedToken.name}</h2>
                <p>{selectedToken.symbol}</p>
              </div>
            </div>
            <div className="modal-body">
              <p className="token-description">{selectedToken.description}</p>
              <div className="modal-stats">
                <div className="modal-stat">
                  <span className="stat-label">Contract Address</span>
                  {/* <span className="stat-value">{selectedToken.address}</span> */}
                  <CopyableMint
                    mintAddress={selectedToken.address}
                    truncate={true}

                    truncateLength={17}
                    fontsize={28}
                  />
                </div>
                <div className="modal-stat">
                  <span className="stat-label">Current Price</span>
                  <span className="stat-value">{formatPrice(selectedToken.price)}</span>
                </div>
                <div className="modal-stat">
                  <span className="stat-label">24h Change</span>
                  <span className={`stat-value ${selectedToken.change24h >= 0 ? 'positive' : 'negative'}`}>
                    {selectedToken.change24h >= 0 ? '+' : ''}{selectedToken.change24h.toFixed(2)}%
                  </span>
                </div>
                <div className="modal-stat">
                  <span className="stat-label">Market Cap</span>
                  <span className="stat-value">{formatMarketCap(selectedToken.marketCap)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trading Modal */}
      <TradingModal
        isOpen={tradingModal.isOpen}
        onClose={closeTradingModal}
        token={tradingModal.token}
        tradeType={tradingModal.tradeType}
      />

      {/* Bot Config Modal */}
      {botConfigModal.isOpen && (
        <BotConfigModal
          isOpen={botConfigModal.isOpen}
          onClose={closeBotConfig}
          token={botConfigModal.token}
        />
      )}

      {/* Price Alert Modal */}
      {alertModal.isOpen && (
        <PriceAlertModal
          isOpen={alertModal.isOpen}
          onClose={closeAlertModal}
          token={alertModal.token}
        />
      )}

      {/* Price Target Modal */}
      {priceTargetModal.isOpen && (
        <PriceTargetModal
          isOpen={priceTargetModal.isOpen}
          onClose={closePriceTargetModal}
          token={priceTargetModal.token}
          initialType={priceTargetModal.type}
        />
      )}

      {/* Add Token Modal */}
      <AddTokenModal
        isOpen={addTokenModal.isOpen}
        onClose={closeAddTokenModal}
      />
    </div>
  );
};

export default SolanaTokensDashboard;
